import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import QuizIcon from '@mui/icons-material/Quiz';

import TuningAdminTable, {
  TuningField,
  boolChip,
  safePreview,
} from './TuningAdminTable';
import {
  AI_MODEL_SELECT_OPTIONS,
  DEFAULT_AI_MODEL_VALUE,
  getAiModelLabel,
} from '../../../../support/aiModelOptions';

const entityTypeOptions = [
  '',
  'product',
  'product_feature',
  'product_component',
  'product_use_case',
  'solution_component',
  'go_to_market',
  'security_use_case',
  'security_use_case_component',
].map((value) => ({
  label: value || 'Any',
  value,
}));

const sectionOptions = [
  '',
  'what',
  'why',
  'sell',
  'configure',
  'validate',
  'differentiate',
  'troubleshoot',
].map((value) => ({
  label: value || 'Any',
  value,
}));

const validationProfileFields: TuningField[] = [
  {
    name: 'key',
    label: 'Key',
    required: true,
    helperText: 'Stable unique lookup key, for example default-rag-grounded-validation.',
  },
  {
    name: 'name',
    label: 'Name',
    required: true,
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    minRows: 3,
  },
  {
    name: 'generation_profile',
    label: 'Generation Profile ID',
    helperText: 'Optional FK to ai_generation_profile. Leave blank for default matching.',
  },
  {
    name: 'entity_type',
    label: 'Entity Type',
    type: 'select',
    options: entityTypeOptions,
  },
  {
    name: 'resource_type',
    label: 'Resource Type',
    type: 'select',
    options: sectionOptions,
    helperText: 'Used for entity-resource validation matching.',
  },
  {
    name: 'section_key',
    label: 'Section Key',
    type: 'select',
    options: sectionOptions,
    helperText: 'Used for snackable/course-section validation matching.',
  },
  {
    name: 'evaluation_model',
    label: 'Evaluation Model',
    type: 'select',
    required: true,
    options: AI_MODEL_SELECT_OPTIONS,
    helperText: `Default: ${DEFAULT_AI_MODEL_VALUE}`,
  },
  {
    name: 'temperature',
    label: 'Temperature',
    type: 'number',
  },
  {
    name: 'publishable_threshold',
    label: 'Publishable Threshold',
    type: 'number',
  },
  {
    name: 'needs_review_threshold',
    label: 'Needs Review Threshold',
    type: 'number',
  },
  {
    name: 'require_all_blocking_questions_pass',
    label: 'Require All Blocking Questions Pass',
    type: 'boolean',
  },
  {
    name: 'validation_prompt_template',
    label: 'Validation Prompt Template',
    type: 'textarea',
    minRows: 8,
  },
  {
    name: 'regeneration_context_template',
    label: 'Regeneration Context Template',
    type: 'textarea',
    minRows: 6,
    helperText: 'Supports {score}, {threshold}, {failure_summary}, and {additional_context}.',
  },
  {
    name: 'is_active',
    label: 'Active',
    type: 'boolean',
  },
];

const buildQuestionFields = (): TuningField[] => [
  {
    name: 'profile',
    label: 'Validation Profile',
    type: 'hidden',
    required: true,
  },
  {
    name: 'key',
    label: 'Key',
    required: true,
    helperText: 'Stable unique key within the selected validation profile.',
  },
  {
    name: 'question',
    label: 'Question',
    type: 'textarea',
    required: true,
    minRows: 4,
  },
  {
    name: 'answer_type',
    label: 'Answer Type',
    type: 'select',
    required: true,
    options: [
      { label: 'score', value: 'score' },
      { label: 'boolean', value: 'boolean' },
      { label: 'text', value: 'text' },
    ],
  },
  {
    name: 'weight',
    label: 'Weight',
    type: 'number',
  },
  {
    name: 'minimum_score',
    label: 'Minimum Score',
    type: 'number',
  },
  {
    name: 'max_score',
    label: 'Max Score',
    type: 'number',
  },
  {
    name: 'is_blocking',
    label: 'Blocking',
    type: 'boolean',
    helperText: 'If enabled, a failed result blocks publishability.',
  },
  {
    name: 'requires_evidence',
    label: 'Requires Evidence',
    type: 'boolean',
  },
  {
    name: 'evaluator_guidance',
    label: 'Evaluator Guidance',
    type: 'textarea',
    minRows: 5,
  },
  {
    name: 'failure_context_template',
    label: 'Failure Context Template',
    type: 'textarea',
    minRows: 4,
    helperText: 'Supports {question_key}, {reasoning}, {missing_evidence}, and {recommended_fix}.',
  },
  {
    name: 'sort_order',
    label: 'Sort Order',
    type: 'number',
  },
  {
    name: 'is_active',
    label: 'Active',
    type: 'boolean',
  },
];

function profileScopeLabel(row: any) {
  const entity = row?.entity_type || 'any';
  const resourceOrSection = row?.resource_type || row?.section_key || 'any';
  return `${entity} / ${resourceOrSection}`;
}

const ValidationProfilesPanel: React.FC = () => {
  const [selectedProfile, setSelectedProfile] = React.useState<any | null>(null);

  const selectedProfileId = selectedProfile?.id || null;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <RuleIcon color="primary" />

          <Box>
            <Typography variant="h6" fontWeight={700}>
              Validation Tuning
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Manage validation profiles and their rubric questions in one place.
              Select a profile on the left to edit its questions on the right.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} xl={5}>
          <TuningAdminTable
            title="Validation Profiles"
            description="Controls critic model settings, thresholds, validation prompt, and regeneration context template."
            endpoint="db/ai-validation-profile/"
            queryKey={['ai-validation-profile']}
            fields={validationProfileFields}
            defaultValues={{
              key: '',
              name: '',
              description: '',
              generation_profile: null,
              entity_type: '',
              resource_type: '',
              section_key: '',
              evaluation_model: DEFAULT_AI_MODEL_VALUE,
              temperature: 0,
              publishable_threshold: 85,
              needs_review_threshold: 70,
              require_all_blocking_questions_pass: true,
              validation_prompt_template: '',
              regeneration_context_template: '',
              is_active: true,
            }}
            selectedId={selectedProfileId}
            onRowClick={(row) => setSelectedProfile(row)}
            orderBy="key"
            rowTitle={(row) => row.name || row.key}
            rowSubtitle={(row) => safePreview(row.description, 180)}
            columns={[
              {
                key: 'profile',
                label: 'Profile',
                render: (row) => (
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight={700}>
                      {row.name || row.key}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {row.key}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Scope: {profileScopeLabel(row)}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Model: {getAiModelLabel(row.evaluation_model)}
                    </Typography>
                  </Stack>
                ),
              },
              {
                key: 'thresholds',
                label: 'Thresholds',
                width: 180,
                render: (row) => (
                  <Stack spacing={0.5}>
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`Pub ${row.publishable_threshold}`}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Review ${row.needs_review_threshold}`}
                    />
                  </Stack>
                ),
              },
              {
                key: 'active',
                label: 'Active',
                width: 100,
                render: (row) => boolChip(row.is_active),
              },
            ]}
          />
        </Grid>

        <Grid item xs={12} xl={7}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <QuizIcon color={selectedProfileId ? 'primary' : 'disabled'} />

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Validation Questions
                  </Typography>

                  {selectedProfile ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={0.75}>
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={selectedProfile.key}
                      />
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`Publishable ${selectedProfile.publishable_threshold}`}
                      />
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`Review ${selectedProfile.needs_review_threshold}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Model ${getAiModelLabel(selectedProfile.evaluation_model)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Scope ${profileScopeLabel(selectedProfile)}`}
                      />
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a validation profile to view and manage its questions.
                    </Typography>
                  )}
                </Box>
              </Stack>

              {!selectedProfileId ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Select a validation profile from the left table. The question
                  editor will then load the rubric questions for that profile.
                </Alert>
              ) : null}
            </Paper>

            <TuningAdminTable
              title={selectedProfile ? `Questions for ${selectedProfile.name || selectedProfile.key}` : 'Validation Questions'}
              description="Rubric questions used by the critic/validation pass."
              endpoint="db/ai-validation-question/"
              queryKey={['ai-validation-question', selectedProfileId || 'none']}
              fields={buildQuestionFields()}
              disabled={!selectedProfileId}
              extraParams={
                selectedProfileId
                  ? {
                    profile: selectedProfileId,
                  }
                  : {}
              }
              defaultValues={{
                profile: selectedProfileId,
                key: '',
                question: '',
                answer_type: 'score',
                weight: 1,
                minimum_score: 75,
                max_score: 100,
                is_blocking: false,
                requires_evidence: true,
                evaluator_guidance: '',
                failure_context_template:
                  'Validation failed for {question_key}: {reasoning}. Missing or weak evidence: {missing_evidence}. Recommended correction: {recommended_fix}.',
                sort_order: 100,
                is_active: true,
              }}
              orderBy="sort_order"
              addLabel="Add Question"
              rowTitle={(row) => row.key}
              rowSubtitle={(row) => safePreview(row.question, 160)}
              columns={[
                {
                  key: 'key',
                  label: 'Question',
                  render: (row) => (
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={700}>
                        {row.key}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {safePreview(row.question, 220)}
                      </Typography>
                    </Stack>
                  ),
                },
                {
                  key: 'score',
                  label: 'Scoring',
                  width: 155,
                  render: (row) => (
                    <Typography variant="caption">
                      weight {row.weight} / min {row.minimum_score}
                    </Typography>
                  ),
                },
                {
                  key: 'blocking',
                  label: 'Blocking',
                  width: 105,
                  render: (row) => boolChip(row.is_blocking),
                },
                {
                  key: 'evidence',
                  label: 'Evidence',
                  width: 105,
                  render: (row) => boolChip(row.requires_evidence),
                },
                {
                  key: 'active',
                  label: 'Active',
                  width: 95,
                  render: (row) => boolChip(row.is_active),
                },
              ]}
            />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ValidationProfilesPanel;